import {Injectable} from '@angular/core';
import {Meter, MeterStream, MeterUtilityType} from '@app/graphql-typings/graphql';
import {SiteInfo} from "@shared/services/api/access-sites.interface";
import {MeterConsumptionType} from '../sitemanager/meters/meters.service';
import {MeterdataType} from '@app/shared/services/api/meter-provider.interface';
import {MapUtils} from '@app/shared/util/collections/collection-utils';

export type UniqueStreamId = string;

@Injectable({
  providedIn: 'root'
})
export class MeterUtilService {

  private readonly METER_STREAM_SEPERATOR = '-';
  private readonly EMISSION_UTILITIES = new Set([MeterUtilityType.ELECTRICITY, MeterUtilityType.GAS]);

  constructor() {}

  isStream(meterOrStream: Meter | MeterStream): meterOrStream is MeterStream {
    return (meterOrStream as MeterStream).stream_id != null;
  }

  buildUniqueStreamId(nmiId: number, streamId: string): UniqueStreamId | null {
    return nmiId != null && streamId != null ? nmiId + this.METER_STREAM_SEPERATOR + streamId : null;
  }

  getNmiAndStreamId(uniqueStreamId: UniqueStreamId): {nmiId: number, streamId: string} | null {
    const meterAndStreamIdArray = uniqueStreamId != null ? uniqueStreamId.split(this.METER_STREAM_SEPERATOR) : null;
    return meterAndStreamIdArray?.length ? {nmiId: +meterAndStreamIdArray[0], streamId: meterAndStreamIdArray[1]} : null;
  }

  buildStreamLabel(meterDisplayName: string, streamDisplayName: string): string | null {
    return meterDisplayName != null && streamDisplayName != null ? meterDisplayName + this.METER_STREAM_SEPERATOR + streamDisplayName : null;
  }

  metersAndStreamsToStreams(metersAndStreams: (Meter | MeterStream)[]) : MeterStream[] {
    const meterStreamsMap = metersAndStreams.reduce((streamMap: Map<string, MeterStream>, current: Meter | MeterStream) => {
      if (current) {
        const streams = this.isStream(current) ? [current] : current.nmi_streams;
        streams.forEach(stream => {
          const currentId = this.buildUniqueStreamId(stream.nmi_id, stream.stream_id);
          if (current != null && !streamMap.has(currentId)) {
            streamMap.set(currentId, stream);
          }
        });
      }
      return streamMap;
    }, new Map<string, MeterStream>());

    return Array.from(meterStreamsMap.values());
  }

  getReportableMetersAndStreamsIds(meters: Meter[]): {meterIds: number[], streamIds: UniqueStreamId[]} {
    const reportableMeterIds: number[] = [];
    const reportableStreamIds: UniqueStreamId[] = [];
    (meters || []).forEach((meter: Meter) => {
      const currentReportableStreamIds: string[] = [];
      (meter.nmi_streams || []).forEach(stream => {
        if (stream?.reportable) {
          currentReportableStreamIds.push(stream.stream_id);
        }
      });
      if (currentReportableStreamIds.length && currentReportableStreamIds.length === meter.nmi_streams?.length) {
        reportableMeterIds.push(meter.nmi_id);
      } else {
        currentReportableStreamIds.forEach(streamId => {
          reportableStreamIds.push(this.buildUniqueStreamId(meter.nmi_id, streamId));
        });
      }
    });
    return {meterIds: reportableMeterIds, streamIds: reportableStreamIds};
  }

  getReportableStreams(sites: SiteInfo[], utilityType?: MeterUtilityType): Map<number, MeterStream[]> {
    const reportableStreamsBySiteId = new Map<number, MeterStream[]>();
    (sites || []).forEach(site => {
      (site.nmi_numbers || []).forEach((meter: Meter) => {
        if (utilityType == null || utilityType == meter.utility_type) {
          (meter.nmi_streams || []).forEach(stream => {
            if (stream?.reportable) {
              stream.nmi_id = meter.nmi_id;
              stream.nmi_number = meter.nmi_number;
              stream.nmi = meter;
              let existingMeterStreams = reportableStreamsBySiteId.get(site.site_id);
              if (existingMeterStreams == null) {
                existingMeterStreams = [];
                reportableStreamsBySiteId.set(site.site_id, existingMeterStreams);
              }
              existingMeterStreams.push(stream);
            }
          });
        }
      });
    });
    return reportableStreamsBySiteId;
  }

  private isAncestorsInSelectedStreams(uniqueStreamId: UniqueStreamId, selectedStreamIds: Set<UniqueStreamId>,
                                        streamsByUniqueStreamId: Map<UniqueStreamId, MeterStream>, metersById: Map<number, Meter>): boolean {
    const stream = streamsByUniqueStreamId.get(uniqueStreamId);
    const meter = metersById.get(stream.nmi_id);
    const parentUniqueStreamId: UniqueStreamId = this.buildUniqueStreamId(meter?.parent_nmi_id, meter?.parent_stream_id);
    if (parentUniqueStreamId != null) {
      if (selectedStreamIds.has(parentUniqueStreamId)) {
        return true;
      } else {
        return this.isAncestorsInSelectedStreams(parentUniqueStreamId, selectedStreamIds, streamsByUniqueStreamId, metersById);
      }
    }
    return false;
  }

  getDescendantStreamIds(streams: MeterStream[], metersById: Map<number, Meter>): Set<string> {
    const descendantStreamIds = new Set<string>();
    const streamsByUniqueStreamId: Map<UniqueStreamId, MeterStream> = new Map();
    metersById.forEach((meter: Meter) => {
      if (meter?.nmi_streams?.length) {
        meter.nmi_streams.forEach(stream => {
          const uniqueStreamId = this.buildUniqueStreamId(stream.nmi_id, stream.stream_id);
          streamsByUniqueStreamId.set(uniqueStreamId, stream);
        });
      }
    });

    const selectedStreamIds = new Set<UniqueStreamId>(streams.map(s => this.buildUniqueStreamId(s.nmi_id, s.stream_id)));
    for (const uniqueStreamId of selectedStreamIds) {
      if (this.isAncestorsInSelectedStreams(uniqueStreamId, selectedStreamIds, streamsByUniqueStreamId, metersById)) {
        descendantStreamIds.add(uniqueStreamId);
      }
    }
    return descendantStreamIds;

  }

  getMeterUtilityTypesThatSupportEmissions(): Set<MeterUtilityType> {
    return this.EMISSION_UTILITIES;
  }

  splitConsumptionTypesByDataType(consumptionTypes: MeterConsumptionType[]): Map<MeterdataType, MeterConsumptionType[]> {
    const resultTypesByDataType = new Map<MeterdataType, MeterConsumptionType[]>();
    consumptionTypes.forEach(consumptionType => {
      const dataType = consumptionType === MeterConsumptionType.DEMAND ? MeterdataType.DEMAND : MeterdataType.ACTIVE_NET;
      const consumptionTypes = MapUtils.setDefault(resultTypesByDataType, dataType, []);
      consumptionTypes.push(consumptionType);
    });
    return resultTypesByDataType;
  }

  flattenConsumptionTypes(consumptionTypesByDataType: Map<MeterdataType, MeterConsumptionType[]>): MeterConsumptionType[] {
    return Array.from(consumptionTypesByDataType.values()).flat();
  }
}
